import React from 'react';
import PropTypes from 'prop-types';
import { Clock, Sun, Sunrise, CloudSun, Moon, Plus } from 'lucide-react';
import { haptic } from '../utils/haptics';
import { TIME_BLOCKS, TIME_BLOCK_ORDER, WEEKDAY_IDS, WEEKDAY_LABELS, blockLabel, blockHoursLabel } from '../utils/timeFormatUtils';

// Icons pro Zeitblock; Uhrzeiten + Beschriftungen kommen aus timeFormatUtils (eine Quelle).
const BLOCK_ICONS = {
  early: <Sunrise className="w-5 h-5 text-warning" />,
  mid: <Sun className="w-5 h-5 text-warning" />,
  late: <CloudSun className="w-5 h-5 text-info" />,
  afternoon: <Plus className="w-5 h-5 text-success" />,
  after: <Moon className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
};

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12"></polyline>
  </svg>
);

const TimeSelector = ({ selectedTimes, onChange }) => {
  const toggleTimeSlot = (day, blockId) => {
    haptic.selection(); // Picker-Tick beim Auswaehlen
    const timeKey = `${day}-${blockId}`;
    const newTimes = { ...selectedTimes };

    if (newTimes[timeKey]) {
      delete newTimes[timeKey];
    } else {
      newTimes[timeKey] = true;
    }

    onChange(newTimes);
  };

  // Prüft, ob ein Zeitslot ausgewählt ist
  const isTimeSelected = (day, blockId) => {
    return selectedTimes[`${day}-${blockId}`] || false;
  };

  return (
    <div
      className="space-y-4 sm:space-y-6"
      onClick={(e) => e.stopPropagation()}
      onSubmit={(e) => e.preventDefault()}
    >
      {/* Mobile-optimierte Schulzeit-Matrix mit Glassmorphism */}
      <div className="bg-surface/90 backdrop-blur-md rounded-xl p-3 sm:p-6 border-2 border-border shadow-token-lg">
        <h3 className="text-sm sm:text-base font-medium text-foreground mb-3 sm:mb-5 flex items-center gap-2 pb-2 border-b border-border">
          <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
          <span>Verfügbarkeit</span>
        </h3>

        <div className="overflow-hidden rounded-xl border-2 border-border bg-surface/80 backdrop-blur-sm shadow-inner">
          {/* Mobile-optimierter Tabellenkopf */}
          <div className="grid grid-cols-6 bg-surface/60 backdrop-blur-sm border-b border-border">
            <div className="p-2 sm:p-3 text-xs sm:text-sm font-medium text-muted-foreground"></div>
            {WEEKDAY_IDS.map(day => (
              <div key={day} className="p-2 sm:p-3 text-center text-xs sm:text-sm font-medium text-foreground/80">
                {day}
              </div>
            ))}
          </div>

          {/* Zeitbloecke inkl. "Nach der Schule" - alle aus TIME_BLOCKS */}
          {TIME_BLOCK_ORDER.map(blockId => {
            const block = TIME_BLOCKS[blockId];
            const label = blockLabel(blockId);
            return (
              <div key={blockId} className="grid grid-cols-6 border-b border-border last:border-b-0 hover:bg-foreground/5 transition-colors duration-200">
                <div className="p-1 sm:p-3 flex items-center gap-0.5 sm:gap-2 text-xs sm:text-sm font-medium text-foreground/80 bg-surface/40 backdrop-blur-sm border-r border-border">
                  <span className="hidden sm:inline">{BLOCK_ICONS[blockId]}</span>
                  <span className="hidden sm:inline truncate">{label}</span>
                  <span className="sm:hidden truncate text-[10px] leading-tight">
                    {block.short}
                    <br />
                    {blockHoursLabel(blockId).replace(' Uhr', '')}
                  </span>
                </div>

                {WEEKDAY_IDS.map(day => {
                  const isSelected = isTimeSelected(day, blockId);
                  return (
                    <div key={`${day}-${blockId}`} className="p-0.5 sm:p-2 flex justify-center items-center">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleTimeSlot(day, blockId);
                        }}
                        title={`${WEEKDAY_LABELS[day]}, ${label}`}
                        aria-label={`${WEEKDAY_LABELS[day]}, ${label}`}
                        aria-pressed={isSelected}
                        className={`h-10 w-10 sm:w-9 sm:h-9 rounded-xl transition-all duration-300 flex items-center justify-center border-2 ${
                          isSelected
                            ? 'bg-primary-soft text-foreground border-primary shadow-token-lg scale-110'
                            : 'bg-surface border-border text-muted-foreground hover:border-primary/50 hover:text-primary hover:scale-105 shadow-token-sm'
                        }`}
                      >
                        {isSelected ? <CheckIcon /> : null}
                      </button>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

TimeSelector.propTypes = {
  selectedTimes: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired
};

export default TimeSelector;
